import React, { useState, useEffect, useCallback } from "react";
import allApi from "../../../api/allApi";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Clock from "react-clock";
import "react-clock/dist/Clock.css";
import Loader from "../common/Loader";
import auditc1 from "../../../assets/images/audit-1.png";
import auditc2 from "../../../assets/images/audit-2.png";
import debounce from "lodash.debounce";
import { PieChart, Pie, Cell, Tooltip } from "recharts";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import { useLocalization } from "../localization/LocalizationContext";
import { useNetwork } from "../common/networkContext";
import NmsTableSearch, { searchRows } from "../common/NmsTableSearch";
import { NMS_CHART_COLORS, useDocumentTheme } from "../common/nmsChartTheme";


function Audit() {
  const { selectedNetworkId, selectedLocation } = useNetwork();
  const networkId = selectedNetworkId?.id ?? 6001; 
  const [selectedRows, setSelectedRows] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [fullDetails, setFullDetails] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [auditLogs, setAuditLogs] = useState([]);
  const [logsPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [refreshing, setRefreshing] = useState(false);
  const [originalAuditLogs, setOriginalAuditLogs] = useState([]);
  const [selectedInterval, setSelectedInterval] = useState("24hr");
  const [selectedEdgeInterval, setSelectedEdgeInterval] = useState("24hr");
  const [selectedUserInterval, setSelectedUserInterval] = useState("24hr");
  const [totalItems, setTotalItems] = useState(1);
  const { localizationStrings } = useLocalization();
  const [license, setLicense] = useState("");
  const [searchActive, setSearchActive] = useState(false);
  const [searchColumn, setSearchColumn] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [tableQuery, setTableQuery] = useState("");
  const [crashQuery, setCrashQuery] = useState("");
  const [activeTab, setActiveTab] = useState("auditLogs");
  const [crashLoading, setCrashLoading] = useState(false);
  const [crashSearchTerms, setCrashSearchTerms] = useState({
    serial_no: "",
    network_id: "",
    created_at: "",
  });
  const [crashSort, setCrashSort] = useState({ field: "", order: "asc" });
  const [crashLogs, setCrashLogs] = useState([]);
  const [crashCurrentPage, setCrashCurrentPage] = useState(1);
  const [crashLogsPerPage] = useState(50);

  const handleIntervalChange = (interval) => {
    setSelectedInterval(interval);
    getDeviceAudit(interval,networkId);
  };
  const handleEdgeIntervalChange = (interval) => {
    setSelectedEdgeInterval(interval);
    getEdgeAudit(interval);
  };
  const handleUserIntervalChange = (interval) => {
    setSelectedUserInterval(interval);
    getUserAudit(interval,networkId);
  };

  const [userAuditData, setUserAuditData] = useState({
    active: 0,
    inactive: 0,
    created: 0,
    deleted: 0,
    updated: 0,
  });
  const [userChartData, setUserChartData] = useState([]);
  const [deviceAuditData, setDeviceAuditData] = useState({
    added: 0,
    deleted: 0,
    updated: 0,
    configPushed: 0,
    firmwareAudits: 0,
  });
  const [deviceChartData, setDeviceChartData] = useState([]);
  const [edgeAuditData, setEdgeAuditData] = useState({
    created: 0,
    updated: 0,
    deleted: 0,
  });
  const [chartData, setChartData] = useState([]);
  const [searchTerms, setSearchTerms] = useState({
    createdTime: "",
    deviceModel: "",
    userId: "",
    deviceId: "",
    actionDetail: "",
  });
  const [sortOrder, setSortOrder] = useState({
    email: "asc",
    actionStatus: "asc",
    createdTime: "asc",
    protocol_type: "asc",
    description: "asc",
    details: "asc",
  });
  const COLORS = NMS_CHART_COLORS;
  const theme = useDocumentTheme();
  const isDark = theme === "dark";
  const chartMuted = isDark ? "#94A3B8" : "#64748B";
  const chartBorder = isDark ? "#1E293B" : "#E2E8F0";

  useEffect(() => {
    if (license && !searchActive) {
      fetchAuditLogs(currentPage,networkId);
    }
  }, [license, currentPage, searchActive,networkId]);

  useEffect(() => {
    const storedLicense = localStorage.getItem("license");
    setLicense(storedLicense);
  }, []);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, []);

  useEffect(() => {
    getUserAudit(selectedUserInterval,networkId);
  }, [localizationStrings,networkId]);

  useEffect(() => {
    getDeviceAudit(selectedInterval,networkId);
  }, [localizationStrings,networkId]);

  useEffect(() => {
    getEdgeAudit(selectedEdgeInterval);
  }, [localizationStrings]);

  useEffect(() => {
    if (activeTab === "crashLogs") {
      fetchCrashLogs(networkId);
    }
    // eslint-disable-next-line
  }, [activeTab,networkId]);

  useEffect(() => {
    setCrashCurrentPage(1);
  }, [crashQuery, crashSort]);

  // function truncateData(data) {
  //   if (!data || data === "null") return "null";

  //   try {
  //     const parsedData = JSON.parse(data);
  //     if (parsedData && parsedData.id) {
  //       return `${parsedData.id}, ${parsedData.name}, ${parsedData.serial_number}`;
  //     } else {
  //       return "Invalid Data";
  //     }
  //   } catch (error) {
  //     return "Invalid Data";
  //   }
  // }

  async function fetchAuditLogs(page) {
    setLoading(true);
    setRefreshing(true);
    try {
      const api = new allApi();
      let response = await api.getRequest(
        `api/audit/all?page=${page}&size=${logsPerPage}&auditType=${license}&networkId=${networkId}`
      );
      if (!response.items) {
        throw new Error("Failed to fetch audit logs");
      }
      setAuditLogs(response.items);
      //  setLogsPerPage(response.length);
      setOriginalAuditLogs(response.items);
      setTotalPages(response.totalPages);
      setTotalItems(response.totalItems);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching audit logs:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  const getUserAudit = async (interval = "24hr") => {
    try {
      const api = new allApi();
      const response = await api.getRequest(`api/audit/user-audit/${interval}?networkId=${networkId}`);
      if (response && Array.isArray(response)) {
        const auditData = {
          active:
            response.find((item) => item.Action === "Logged_In_User")?.Count ||
            0,
          inactive:
            response.find((item) => item.Action === "User_Inactive")?.Count ||
            0,
          created:
            response.find((item) => item.Action === "User_Created")?.Count || 0,
          deleted:
            response.find((item) => item.Action === "User_Deleted")?.Count || 0,
          updated:
            response.find((item) => item.Action === "User_Updated")?.Count || 0,
        };
        setUserAuditData(auditData);
        setUserChartData([
          { name: localizationStrings?.loggedIn, value: auditData.active },
          { name: localizationStrings?.inactive, value: auditData.inactive },
          { name: localizationStrings?.created, value: auditData.created },
          { name: localizationStrings?.deleted, value: auditData.deleted },
          { name: localizationStrings?.updated, value: auditData.updated },
        ]);
      }
    } catch (error) {
      console.error("Error fetching user audit data", error);
    }
  };
  const getDeviceAudit = async (interval = "24hr") => {
    try {
      const api = new allApi();
      const response = await api.getRequest(
        `api/audit/device-audit/${interval}?networkId=${networkId}`
      );
      if (response && Array.isArray(response)) {
        const auditData = {
          added:
            response.find((item) => item.Action === "DEVICE_ADDED")?.Count || 0,
          deleted:
            response.find((item) => item.Action === "DEVICE_DELETED")?.Count ||
            0,
          updated:
            response.find((item) => item.Action === "DEVICE_UPDATED")?.Count ||
            0,
          configPushed:
            response.find((item) => item.Action === "CONFIG_PUSHED")?.Count ||
            0,
          firmwareAudits:
            response.find((item) => item.Action === "FIRMWARE_AUDITS")?.Count ||
            0,
        };
        setDeviceAuditData(auditData);
        setDeviceChartData([
          { name: localizationStrings?.added, value: auditData.added },
          { name: localizationStrings?.deleted, value: auditData.deleted },
          { name: localizationStrings?.updated, value: auditData.updated },
          { name: localizationStrings?.config, value: auditData.configPushed },
          {
            name: localizationStrings?.fwAudits,
            value: auditData.firmwareAudits,
          },
        ]);
      }
    } catch (error) {
      console.error("Error fetching device audit data", error);
    }
  };

  const fetchCrashLogs = async () => {
    setCrashLoading(true);
    try {
      const api = new allApi();
      const data = await api.getRequest(
        `api/crash/networks/crash-logs?networkId=${networkId}`
      );
      setCrashLogs(Array.isArray(data.response) ? data.response : []);
    } catch (error) {
      setCrashLogs([]);
      console.error("Error fetching crash logs:", error);
    } finally {
      setCrashLoading(false);
    }
  };

  const totalActions =
    deviceAuditData.added +
    deviceAuditData.deleted +
    deviceAuditData.updated +
    deviceAuditData.configPushed +
    deviceAuditData.firmwareAudits;

  const percentage =
    totalActions > 0
      ? ((deviceAuditData.configPushed / totalActions) * 100).toFixed(2)
      : 0;

  const getEdgeAudit = async (interval = "24hr") => {
    try {
      const api = new allApi();
      const response = await api.getRequest(`api/audit/edge-audit/${interval}`);
      if (response && Array.isArray(response)) {
        const auditData = {
          created:
            response.find((item) => item.Action === "EDGE_ADDED")?.Count || 0,
          updated:
            response.find((item) => item.Action === "EDGE_UPDATED")?.Count || 0,
          deleted:
            response.find((item) => item.Action === "EDGE_DELETED")?.Count || 0,
        };
        setEdgeAuditData(auditData);
        setChartData([
          { name: localizationStrings?.created, value: auditData.created },
          { name: localizationStrings?.updated, value: auditData.updated },
          { name: localizationStrings?.deleted, value: auditData.deleted },
        ]);
      }
    } catch (error) {
      console.error("Error fetching audit data", error);
    }
  };

  const handleSelectAllCheckbox = (event) => {
    if (event.target.checked) {
      const allIndexes = Array.from(Array(auditLogs.length).keys());
      setSelectedRows(allIndexes);
    } else {
      setSelectedRows([]);
    }
  };

  const selectAllChecked = selectedRows.length === auditLogs.length;

  const toggleRowSelection = (index) => {
    if (selectedRows.includes(index)) {
      setSelectedRows(selectedRows.filter((item) => item !== index));
    } else {
      setSelectedRows([...selectedRows, index]);
    }
  };

  const togglePopup = (details) => {
    setShowPopup((prevShowPopup) => !prevShowPopup);
    setFullDetails(details);
  };

  // const handleMouseEnter = () => {
  //   setShowPopup(true);
  // };

  // const handleMouseLeave = () => {
  //   setShowPopup(false);
  // };

  const toggleSwitch = () => {
    setIsEnabled(!isEnabled);
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
    if (searchActive) {
      debouncedSearch(pageNumber, searchColumn, searchValue);
    } else {
      fetchAuditLogs(pageNumber,networkId);
    }
  };

  const handleLastPage = () => {
    setCurrentPage(totalPages);
    fetchAuditLogs(totalPages,networkId);
  };

  const handleRefresh = () => {
    fetchAuditLogs(currentPage,networkId);
  };

  const handleSearchFilterByName = async (page, name, value) => {
    //debugger
    setLoading(true);
    try {
      const api = new allApi();
      const response = await api.getRequest(
        `api/audit/all?page=${page}&size=50&column=${name}&value=${value}&auditType=${license}`
      );
      if (!response.items) {
        throw new Error("Failed to fetch filtered audit logs");
      }
      setAuditLogs(response.items);
      setTotalPages(response.totalPages);
      setTotalItems(response.totalItems);
    } catch (error) {
      console.error("Error fetching filtered audit logs:", error);
    } finally {
      setLoading(false);
    }
  };

  const debouncedSearch = useCallback(
    debounce((page, name, value) => {
      if (license) {
        handleSearchFilterByName(page, name, value);
      }
    }, 800),
    [license]
  );

  const handleSearch = (e) => {
    const { name, value } = e.target;
    handleTableSearch(value, name);
  };

  const handleTableSearch = (value, column = "email") => {
    setTableQuery(value);
    if (value) {
      const newPage = 1;
      setSearchActive(true);
      setSearchColumn(column);
      setSearchValue(value);
      setCurrentPage(newPage);
      debouncedSearch(newPage, column, value);
    } else {
      setSearchActive(false);
      setSearchColumn("");
      setSearchValue("");
      setAuditLogs(originalAuditLogs);
      setTotalItems(originalAuditLogs.length);
      setTotalPages(Math.ceil(originalAuditLogs.length / logsPerPage));
    }
  };

  const handleSearchBox = (header) => {
    console.log("Clicked on header:", header);
  };

  const indexOfLastLog = currentPage * logsPerPage;
  const indexOfFirstLog = indexOfLastLog - logsPerPage;
  const totalFilteredLogs = auditLogs.length;
  const currentLogs = auditLogs.slice(indexOfFirstLog, indexOfLastLog);

  const getPageNumbers = () => {
    const range = 1;
    const start = Math.max(1, currentPage - range);
    const end = Math.min(totalPages, currentPage + range);

    return [...Array(end - start + 1).keys()].map((i) => start + i);
  };

  const handleSortChange = (field) => {
    // Toggle the sort order for the clicked field
    const newOrder = sortOrder[field] === "asc" ? "desc" : "asc";

    // Sort the gateways based on the field and order
    const sortedLogs = [...auditLogs].sort((a, b) => {
      // Handle undefined values gracefully
      const aValue = a[field] ? a[field].toString().toLowerCase() : "";
      const bValue = b[field] ? b[field].toString().toLowerCase() : "";

      if (newOrder === "asc") {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });

    // Update the state with the new order and sorted gateways
    setAuditLogs(sortedLogs);
    setSortOrder((prevState) => ({
      ...prevState,
      [field]: newOrder, // Set the new sort order for the field
    }));
  };

  const CustomXAxisTick = ({ x, y, payload }) => {
    const maxCharsPerLine = 8;
    const text = payload.value || "";

    // Split text into chunks of 6 characters
    const lines = text.match(new RegExp(`.{1,${maxCharsPerLine}}`, "g"));

    return (
      <g transform={`translate(${x},${y + 10})`}>
        <text textAnchor="middle" fill={chartMuted}>
          {lines &&
            lines.map((line, index) => (
              <tspan x={0} dy={index === 0 ? 0 : 14} key={index}>
                {line}
              </tspan>
            ))}
        </text>
      </g>
    );
  };

  // Filtering
  const filteredCrashLogs = searchRows(crashLogs, crashQuery, [
    "serial_no",
    "network_id",
    "created_at",
  ]);

  // Sorting
  const sortedCrashLogs = [...filteredCrashLogs].sort((a, b) => {
    if (!crashSort.field) return 0;
    let aValue = a[crashSort.field];
    let bValue = b[crashSort.field];
    if (crashSort.field === "created_at") {
      aValue = aValue ? new Date(aValue).getTime() : 0;
      bValue = bValue ? new Date(bValue).getTime() : 0;
      return crashSort.order === "asc" ? aValue - bValue : bValue - aValue;
    }
    aValue = aValue ? aValue.toString().toLowerCase() : "";
    bValue = bValue ? bValue.toString().toLowerCase() : "";
    if (crashSort.order === "asc") return aValue.localeCompare(bValue);
    return bValue.localeCompare(aValue);
  });

  const startIndex = (crashCurrentPage - 1) * crashLogsPerPage;
  const endIndex = startIndex + crashLogsPerPage;

  const currentCrashLogs = sortedCrashLogs.slice(startIndex, endIndex);

  const crashTotalPages = Math.ceil(sortedCrashLogs.length / crashLogsPerPage);
  const handleCrashPageClick = (pageNumber) => {
    setCrashCurrentPage(pageNumber);
  };
  const getCrashPageNumbers = () => {
    const range = 1;
    const start = Math.max(1, crashCurrentPage - range);
    const end = Math.min(crashTotalPages, crashCurrentPage + range);

    return [...Array(end - start + 1).keys()].map((i) => start + i);
  };

  return (
    <div className="nms-dash audits-page">
      <div className="row mb-4">
        <div className="col-lg-4">
          <div className="inc-card mb-4">
            <div className="inc-card-title">
              <h3>{localizationStrings?.edge}</h3>
              <span
                className="dropdown"
                id="edgeDropdown"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <span>
                  {selectedEdgeInterval}
                  <i className="fa fa-angle-down" aria-hidden="true"></i>
                </span>
                <ul
                  className="dropdown-menu dropdown-menu-end"
                  aria-labelledby="edgeDropdown"
                >
                  {["8hr", "24hr", "7days", "30days", "3months", "6months"].map(
                    (interval) => (
                      <li key={interval}>
                        <a
                          className="dropdown-item"
                          type="button"
                          onClick={() => handleEdgeIntervalChange(interval)}
                        >
                          {interval}
                        </a>
                      </li>
                    )
                  )}
                </ul>
              </span>
            </div>
            <div className="inc-card-body">
              <div>
                <div className="deviceCountChart audit-edge-chart">
                  <div className="device-chart">
                    <div>
                      {chartData && chartData.length > 0 ? (
                        <div className="c-chart">
                          <ResponsiveContainer aspect={2.3}>
                            <BarChart
                              width={400}
                              height={200}
                              data={chartData}
                              margin={{
                                top: 20,
                                right: 30,
                                left: -20,
                                bottom: 5,
                              }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke={chartBorder} />
                              <XAxis dataKey="name" stroke={chartMuted} tick={{ fill: chartMuted, fontSize: 11 }} />
                              <YAxis stroke={chartMuted} tick={{ fill: chartMuted, fontSize: 11 }} />
                              <Tooltip
                                contentStyle={{
                                  background: isDark ? "#111827" : "#FFFFFF",
                                  border: `1px solid ${chartBorder}`,
                                  color: isDark ? "#F8FAFC" : "#0F172A",
                                }}
                              />
                              <Bar dataKey="value" fill="#82ca9d">
                                {chartData.map((entry, index) => (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={COLORS[index % COLORS.length]}
                                  />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      ) : (
                        <div className="nms-empty">{localizationStrings?.noData}</div>
                      )}
                    </div>
                  </div>
                  <div className="c-legend">
                    <ul>
                      {chartData.map((entry, index) => (
                        <li key={index}>
                          <span
                            className="legend-color"
                            style={{
                              backgroundColor: COLORS[index % COLORS.length],
                            }}
                          ></span>
                          {entry.name} : <strong>{entry.value}</strong>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* <div className="col-lg-3">
          <div className="inc-card mb-4">
            <div className="inc-card-body">
              <div className="card-top">
                <div>
                  <h3>Tenant</h3>
                </div>
                <div>
                  <span className="a-yellow">
                    <i className="fa fa-pie-chart" aria-hidden="true"></i>
                  </span>
                </div>
              </div>
              <div className="audit-card-count aud-yellow">
                <div>
                  <div>
                    <span></span>
                    <strong>13</strong>Active
                  </div>
                  <div>
                    <span></span>
                    <strong>28</strong>Created
                  </div>
                </div>
                <div>
                  <div>
                    <span></span>
                    <strong>30</strong>Deleted
                  </div>
                  <div>
                    <span></span>
                    <strong>67</strong>Updated
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="inc-card">
            <div className="inc-card-body">
              <div className="card-top">
                <div>
                  <h3>MQTT</h3>
                </div>
                <div>
                  <span className="a-blue">
                    <i className="fa fa-pie-chart" aria-hidden="true"></i>
                  </span>
                </div>
              </div>
              <div className="audit-card-count aud-blue">
                <div>
                  <div>
                    <span></span>
                    <strong>45</strong>No of connections
                  </div>
                </div>
                <div>
                  <div>
                    <span></span>
                    <strong>44</strong>Data bytes transfered
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div> */}
        <div className="col-lg-4">
          <div className="inc-card">
            <div className="inc-card-title">
              <h3>{localizationStrings?.userAudit}</h3>
              <span
                className="dropdown"
                id="edgeDropdown"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <span>
                  {selectedUserInterval}
                  <i className="fa fa-angle-down" aria-hidden="true"></i>
                </span>
                <ul
                  className="dropdown-menu dropdown-menu-end"
                  aria-labelledby="edgeDropdown"
                >
                  {["8hr", "24hr", "7days", "30days", "3months", "6months"].map(
                    (interval) => (
                      <li key={interval}>
                        <a
                          className="dropdown-item"
                          href="#"
                          onClick={() => handleUserIntervalChange(interval)}
                        >
                          {interval}
                        </a>
                      </li>
                    )
                  )}
                </ul>
              </span>
            </div>
            <div className="inc-card-body">
              <div>
                <div className="deviceCountChart">
                  <div className="device-chart">
                    <div>
                      {userChartData && userChartData.length > 0 ? (
                        <div className="c-chart">
                          <ResponsiveContainer aspect={2.3}>
                            <BarChart
                              width={400}
                              height={200}
                              data={userChartData}
                              margin={{
                                top: 20,
                                right: 30,
                                left: 0,
                                bottom: 5,
                              }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke={chartBorder} />
                              <XAxis dataKey="name" interval={0} stroke={chartMuted} tick={{ fill: chartMuted, fontSize: 11 }} />
                              <YAxis stroke={chartMuted} tick={{ fill: chartMuted, fontSize: 11 }} />
                              <Tooltip
                                contentStyle={{
                                  background: isDark ? "#111827" : "#FFFFFF",
                                  border: `1px solid ${chartBorder}`,
                                  color: isDark ? "#F8FAFC" : "#0F172A",
                                }}
                              />
                              <Bar dataKey="value" fill="#8884d8">
                                {userChartData.map((entry, index) => (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={COLORS[index % COLORS.length]}
                                  />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      ) : (
                        <div className="nms-empty">{localizationStrings?.noData}</div>
                      )}
                    </div>
                  </div>
                  <div className="c-legend">
                    <ul>
                      {userChartData.map((entry, index) => (
                        <li key={index}>
                          <span
                            className="legend-color"
                            style={{
                              backgroundColor: COLORS[index % COLORS.length],
                            }}
                          ></span>
                          {entry.name} : <strong>{entry.value}</strong>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-4">
          <div className="inc-card">
            <div className="inc-card-title">
              <h3>{localizationStrings?.deviceAudit}</h3>
              <span
                className="dropdown"
                id="edgeDropdown"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <span>
                  {selectedInterval}
                  <i className="fa fa-angle-down" aria-hidden="true"></i>
                </span>
                <ul
                  className="dropdown-menu dropdown-menu-end"
                  aria-labelledby="edgeDropdown"
                >
                  {["8hr", "24hr", "7days", "30days", "3months", "6months"].map(
                    (interval) => (
                      <li key={interval}>
                        <a
                          className="dropdown-item"
                          href="#"
                          onClick={() => handleIntervalChange(interval)}
                        >
                          {interval}
                        </a>
                      </li>
                    )
                  )}
                </ul>
              </span>
            </div>
            <div className="inc-card-body">
              <div>
                <div className="deviceCountChart">
                  <div className="device-chart">
                    <div>
                      {deviceChartData && deviceChartData.length > 0 ? (
                        <div className="c-chart">
                          <ResponsiveContainer aspect={2.3}>
                            <BarChart
                              width={400}
                              height={200}
                              data={deviceChartData}
                              margin={{
                                top: 20,
                                right: 30,
                                left: -20,
                                bottom: 5,
                              }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke={chartBorder} />
                              <XAxis
                                dataKey="name"
                                interval={0}
                                tick={<CustomXAxisTick />}
                                //angle={-20}
                                //textAnchor="end"
                              />
                              <YAxis stroke={chartMuted} tick={{ fill: chartMuted, fontSize: 11 }} />
                              <Tooltip
                                contentStyle={{
                                  background: isDark ? "#111827" : "#FFFFFF",
                                  border: `1px solid ${chartBorder}`,
                                  color: isDark ? "#F8FAFC" : "#0F172A",
                                }}
                              />
                              <Bar dataKey="value" fill="#8884d8">
                                {deviceChartData.map((entry, index) => (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={COLORS[index % COLORS.length]}
                                  />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      ) : (
                        <div className="nms-empty">{localizationStrings?.noData}</div>
                      )}
                    </div>
                  </div>
                  <div className="c-legend">
                    <ul>
                      {deviceChartData.map((entry, index) => (
                        <li key={index}>
                          <span
                            className="legend-color"
                            style={{
                              backgroundColor: COLORS[index % COLORS.length],
                            }}
                          ></span>
                          {entry.name} : <strong>{entry.value}</strong>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="inc-card audit-table">
        <div className="inc-card-title">
          <div className="ts-tabs" role="tablist">
            <button
              type="button"
              className={`ts-tab${activeTab === "auditLogs" ? " is-active" : ""}`}
              onClick={() => setActiveTab("auditLogs")}
            >
              {localizationStrings?.auditLogs || "Audit Logs"}
            </button>
            <button
              type="button"
              className={`ts-tab${activeTab === "crashLogs" ? " is-active" : ""}`}
              onClick={() => setActiveTab("crashLogs")}
            >
              Crash Logs
            </button>
          </div>
          <div className="inc-card-button">
            {/* <span className="filter-pop">
              <button
                className="icon-btn"
                title="filter"
                onClick={() => setIsOpen(!isOpen)}
              >
                {" "}
                <i className="fa fa-filter" aria-hidden="true"></i>
              </button>
              {isOpen && (
                <div className="device-filter" aria-labelledby="deviceFilter">
                  <div className="filter-title">
                    <span>Filter</span>
                    <a href="#">Clear</a>
                  </div>
                  <div className="filter-body">
                    <ul>
                      <li>
                        <label>
                          <input type="radio" name="radio" /> Last
                        </label>
                        <div className="row">
                          <div className="col-lg-10">
                            <select>
                              <option>1 Day</option>
                              <option>2 Day</option>
                              <option>3 Day</option>
                              <option>4 Day</option>
                            </select>
                          </div>
                          <label className="switch">
                            <input
                              type="checkbox"
                              checked={isEnabled}
                              onChange={toggleSwitch}
                            />
                            <span className="slider round"></span>
                          </label>
                          {isEnabled && (
                            <div className="col-lg-10 last-filter">
                              <input
                                type="text"
                                className="cal-input"
                                placeholder="Day"
                              />
                              <input
                                type="text"
                                className="cal-input"
                                placeholder="Hr"
                              />
                              <input
                                type="text"
                                className="cal-input"
                                placeholder="Min"
                              />
                              <input
                                type="text"
                                className="cal-input"
                                placeholder="sec"
                              />
                            </div>
                          )}
                        </div>
                      </li>
                      <li>
                        <label>
                          <input type="radio" name="radio" /> Time period
                        </label>
                        <div className="row">
                          <div className="col-lg-6">
                            <DatePicker
                              selected={startDate}
                              onChange={(date) => setStartDate(date)}
                              className="cal-input"
                            />
                          </div>
                          <div className="col-lg-6">
                            <DatePicker
                              selected={endDate}
                              onChange={(date) => setEndDate(date)}
                              className="cal-input"
                            />{" "}
                          </div>
                        </div>
                      </li>
                      <li>
                        <label>
                          <input type="radio" name="radio" /> Interval
                        </label>
                        <select>
                          <option>1 Day</option>
                          <option>2 Day</option>
                          <option>3 Day</option>
                          <option>4 Day</option>
                        </select>
                      </li>
                    </ul>
                  </div>
                  <div className="filter-button">
                    <button
                      className="lineBtn"
                      onClick={() => setIsOpen(false)}
                    >
                      Cancel
                    </button>
                    <button className="fillBtn">Apply</button>
                  </div>
                </div>
              )}
            </span> */}
            <NmsTableSearch
              value={activeTab === "crashLogs" ? crashQuery : tableQuery}
              onChange={(value) => {
                if (activeTab === "crashLogs") {
                  setCrashQuery(value);
                  setCrashCurrentPage(1);
                } else {
                  handleTableSearch(value, "email");
                }
              }}
              placeholder={localizationStrings?.search || "Search"}
            />
            <button
              className="icon-btn"
              title="Refresh"
              onClick={handleRefresh}
            >
              {refreshing ? (
                <i className="fa fa-spinner fa-spin" aria-hidden="true"></i>
              ) : (
                <i className="fa fa-refresh" aria-hidden="true"></i>
              )}
            </button>
          </div>
        </div>
        <div className="inc-card-body">
          <div className="tab-content" id="auditTabContent">
            <div
              className={`tab-pane fade ${
                activeTab === "auditLogs" ? "show active" : ""
              }`}
              id="audit-logs-tab-pane"
              role="tabpanel"
              aria-labelledby="audit-logs-tab"
            >
              <div className="inc-card-table half">
                <table className="full-table m-head">
                  <thead>
                    <tr>
                      <th>
                        <label className="checkbox">
                          <input
                            type="checkbox"
                            onChange={handleSelectAllCheckbox}
                          />
                          <span className="indicator"></span>
                        </label>
                      </th>
                      {/* <th>Audit ID</th> */}
                      <th
                        className="inc-th-sort"
                        onClick={() => handleSortChange("email")}
                      >
                        {localizationStrings?.userEmail}
                        <i className="fa fa-sort" aria-hidden="true" />
                      </th>
                      <th
                        className="inc-th-sort"
                        onClick={() => handleSortChange("actionStatus")}
                      >
                        {localizationStrings?.actionStatus}
                        <i className="fa fa-sort" aria-hidden="true" />
                      </th>
                      <th
                        className="inc-th-sort"
                        onClick={() => handleSortChange("createdTime")}
                      >
                        {localizationStrings?.logTime}
                        <i className="fa fa-sort" aria-hidden="true" />
                      </th>
                      {/* <th>{localizationStrings?.deviceType}</th> */}
                      <th
                        className="inc-th-sort"
                        onClick={() => handleSortChange("description")}
                      >
                        {localizationStrings?.description}
                        <i className="fa fa-sort" aria-hidden="true" />
                      </th>
                    </tr>
                    <tr className="search-border">
                      <td></td>
                      {/* <td>
                    <input
                      className="searchBox smallWidth"
                      type="text"
                      name="auditId"
                      onKeyUp={(e) => handleSearch(e)}
                    />
                    <i
                      className="fa fa-sort-amount-desc"
                      onClick={() => handleSearchBox("auditId")}
                      aria-hidden="true"
                    ></i>
                  </td> */}
                      <td>
                        <input
                          className="searchBox smallWidth"
                          type="text"
                          name="email"
                          onKeyUp={(e) => handleSearch(e)}
                        />{" "}
                        <i
                          className={`fa ${
                            sortOrder.email === "asc"
                              ? "fa fa-sort-amount-desc"
                              : "fa fa-sort-amount-asc"
                          }`}
                          onClick={() => handleSortChange("email")}
                          aria-hidden="true"
                        ></i>
                      </td>
                      <td>
                        <input
                          className="searchBox smallWidth"
                          type="text"
                          name="actionStatus"
                          onKeyUp={(e) => handleSearch(e)}
                        />{" "}
                        <i
                          className={`fa ${
                            sortOrder.actionStatus === "asc"
                              ? "fa fa-sort-amount-desc"
                              : "fa fa-sort-amount-asc"
                          }`}
                          onClick={() => handleSortChange("actionStatus")}
                          aria-hidden="true"
                        ></i>
                      </td>
                      <td>
                        <input
                          className="searchBox smallWidth"
                          type="text"
                          name="createdTime"
                          onKeyUp={(e) => handleSearch(e)}
                        />
                        <i
                          className={`fa ${
                            sortOrder.createdTime === "asc"
                              ? "fa fa-sort-amount-desc"
                              : "fa fa-sort-amount-asc"
                          }`}
                          onClick={() => handleSortChange("createdTime")}
                          aria-hidden="true"
                        ></i>
                      </td>
                      {/* <td>
                    <input
                      className="searchBox smallWidth"
                      type="text"
                      name="deviceType"
                      onKeyUp={(e) => handleSearch(e)}
                    />
                    <i
                      className={`fa ${
                        sortOrder.details === "asc"
                          ? "fa fa-sort-amount-desc"
                          : "fa fa-sort-amount-asc"
                      }`}
                      onClick={() => handleSortChange("details")}
                      aria-hidden="true"
                    ></i>
                  </td> */}
                      <td>
                        <input
                          className="searchBox smallWidth"
                          type="text"
                          name="description"
                          onKeyUp={(e) => handleSearch(e)}
                        />
                        <i
                          className={`fa ${
                            sortOrder.description === "asc"
                              ? "fa fa-sort-amount-desc"
                              : "fa fa-sort-amount-asc"
                          }`}
                          onClick={() => handleSortChange("description")}
                          aria-hidden="true"
                        ></i>
                      </td>
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="6">
                          <Loader />
                        </td>
                      </tr>
                    ) : auditLogs.length === 0 ? (
                      <tr>
                        <td colSpan="6" style={{ textAlign: "center" }}>
                          {localizationStrings?.noData || "No data to display"}
                        </td>
                      </tr>
                    ) : (
                      auditLogs.map((log, index) => (
                        <tr key={index}>
                          <td>
                            <label className="checkbox">
                              <input
                                type="checkbox"
                                checked={selectedRows.includes(index)}
                                onChange={() => toggleRowSelection(index)}
                              />
                              <span className="indicator"></span>
                            </label>
                          </td>
                          <td>{log.email || "N/A"}</td>
                          <td>
                            <span className={`ota-status ${(log.actionStatus || "").toLowerCase()}`}>
                              {log.actionStatus || "N/A"}
                            </span>
                          </td>
                          <td>{log.createdTime || "N/A"}</td>
                          <td>{log.description || "N/A"}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div
              className={`tab-pane fade ${
                activeTab === "crashLogs" ? "show active" : ""
              }`}
              id="crash-logs-tab-pane"
              role="tabpanel"
              aria-labelledby="crash-logs-tab"
            >
              <div className="inc-card-table half">
                <table className="full-table m-head">
                  <thead>
                    <tr>
                      <th>Sr. No.</th>
                      <th
                        className="inc-th-sort"
                        onClick={() =>
                          setCrashSort({
                            field: "serial_no",
                            order:
                              crashSort.field === "serial_no" &&
                              crashSort.order === "asc"
                                ? "desc"
                                : "asc",
                          })
                        }
                      >
                        Device Serial
                        <i className="fa fa-sort" aria-hidden="true" />
                      </th>
                      <th
                        className="inc-th-sort"
                        onClick={() =>
                          setCrashSort({
                            field: "network_id",
                            order:
                              crashSort.field === "network_id" &&
                              crashSort.order === "asc"
                                ? "desc"
                                : "asc",
                          })
                        }
                      >
                        Network ID
                        <i className="fa fa-sort" aria-hidden="true" />
                      </th>
                      <th
                        className="inc-th-sort"
                        onClick={() =>
                          setCrashSort({
                            field: "created_at",
                            order:
                              crashSort.field === "created_at" &&
                              crashSort.order === "asc"
                                ? "desc"
                                : "asc",
                          })
                        }
                      >
                        Crash Time
                        <i className="fa fa-sort" aria-hidden="true" />
                      </th>
                      <th>Download</th>
                    </tr>
                    <tr className="search-border">
                      <td></td>
                      <td>
                        <input
                          className="searchBox smallWidth"
                          type="text"
                          name="serial_no"
                          placeholder="Search Serial"
                          value={crashSearchTerms.serial_no}
                          onChange={(e) =>
                            setCrashSearchTerms((prev) => ({
                              ...prev,
                              serial_no: e.target.value,
                            }))
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="searchBox smallWidth"
                          type="text"
                          name="network_id"
                          placeholder="Search Network"
                          value={crashSearchTerms.network_id}
                          onChange={(e) =>
                            setCrashSearchTerms((prev) => ({
                              ...prev,
                              network_id: e.target.value,
                            }))
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="searchBox smallWidth"
                          type="text"
                          name="created_at"
                          placeholder="Search Time"
                          value={crashSearchTerms.created_at}
                          onChange={(e) =>
                            setCrashSearchTerms((prev) => ({
                              ...prev,
                              created_at: e.target.value,
                            }))
                          }
                        />
                      </td>
                      <td></td>
                    </tr>
                  </thead>
                  <tbody>
                    {crashLoading ? (
                      <tr>
                        <td colSpan="5" style={{ textAlign: "center" }}>
                          <Loader />
                        </td>
                      </tr>
                    ) : sortedCrashLogs.length === 0 ? (
                      <tr>
                        <td colSpan="5" style={{ textAlign: "center" }}>
                          No crash logs found
                        </td>
                      </tr>
                    ) : (
                      currentCrashLogs.map((log, idx) => (
                        <tr key={log.crash_id || idx}>
                          <td>{startIndex + idx + 1}</td>{" "}
                          <td>{log.serial_no || "N/A"}</td>
                          <td>{log.network_id || "N/A"}</td>
                          <td>
                            {log.created_at
                              ? new Date(log.created_at).toLocaleString()
                              : "N/A"}
                          </td>
                          <td>
                            <a
                              href={log.crash_url}
                              className="icon-btn"
                              target="_blank"
                              rel="noopener noreferrer"
                              download
                              title="Download Crash File"
                            >
                              <i className="fa fa-download" />
                            </a>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div className="pagination-container">
          {activeTab === "auditLogs" ? (
            <>
              {/* ✅ AUDIT LOG PAGINATION */}
              <div className="pagination-info">
                <p>
                  {localizationStrings?.showing}{" "}
                  {totalItems === 0
                    ? "0–0"
                    : `${indexOfFirstLog + 1}–${Math.min(
                        currentPage * logsPerPage,
                        totalItems
                      )}`}{" "}
                  {localizationStrings?.of} {totalItems}{" "}
                  {localizationStrings?.records}
                </p>
              </div>

              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    onClick={() => handlePageClick(1)}
                    disabled={currentPage === 1}
                  >
                    {localizationStrings?.firstPage}
                  </button>

                  <button
                    onClick={() => handlePageClick(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    {localizationStrings?.previous}
                  </button>

                  {getPageNumbers().map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageClick(page)}
                      className={currentPage === page ? "active" : ""}
                    >
                      {page}
                    </button>
                  ))}

                  <button
                    onClick={() => handlePageClick(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    {localizationStrings?.next}
                  </button>

                  <button
                    onClick={handleLastPage}
                    disabled={currentPage === totalPages}
                  >
                    {localizationStrings?.lastPage}
                  </button>
                </div>
              )}
            </>
          ) : (
            <>
              {/* ✅ CRASH LOG PAGINATION */}
              <div className="pagination-info">
                <p>
                  Showing{" "}
                  {sortedCrashLogs.length === 0
                    ? "0–0"
                    : `${startIndex + 1}–${Math.min(
                        endIndex,
                        sortedCrashLogs.length
                      )}`}{" "}
                  of {sortedCrashLogs.length} records
                </p>
              </div>

              {crashTotalPages > 1 && (
                <div className="pagination">
                  <button
                    onClick={() => handleCrashPageClick(1)}
                    disabled={crashCurrentPage === 1}
                  >
                    First
                  </button>

                  <button
                    onClick={() => handleCrashPageClick(crashCurrentPage - 1)}
                    disabled={crashCurrentPage === 1}
                  >
                    Prev
                  </button>

                  {getCrashPageNumbers().map((page) => (
                    <button
                      key={page}
                      onClick={() => handleCrashPageClick(page)}
                      className={crashCurrentPage === page ? "active" : ""}
                    >
                      {page}
                    </button>
                  ))}

                  <button
                    onClick={() => handleCrashPageClick(crashCurrentPage + 1)}
                    disabled={crashCurrentPage === crashTotalPages}
                  >
                    Next
                  </button>

                  <button
                    onClick={() => handleCrashPageClick(crashTotalPages)}
                    disabled={crashCurrentPage === crashTotalPages}
                  >
                    Last
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Audit;
